import useSubscriptionStatus from './useSubscriptionStatus';

interface PlanLimits {
  maxGroups: number;
  maxPlayersPerGroup: number;
  hasResenha: boolean;
  canCreateGame: boolean;
  hasPrioritySupport: boolean;
  supportPhone: string;
  durationMonths: number;
  planName: string;
  planType: string;
}

export const usePlanFeatures = () => {
  const { 
    isTrialActive, 
    isSubscribed, 
    isLifetime,
    subscription, 
    loading, 
    error 
  } = useSubscriptionStatus();

  const getPlanLimits = (): PlanLimits => {
    // Se o usuário tem acesso lifetime (admin)
    if (isLifetime) {
      return {
        maxGroups: 5,
        maxPlayersPerGroup: 200,
        hasResenha: true,
        canCreateGame: true,
        hasPrioritySupport: true,
        supportPhone: '5511916158282',
        durationMonths: 0, // Vitalício
        planName: 'Lifetime',
        planType: 'lifetime'
      };
    }
    
    // Se o usuário tem uma assinatura ativa
    if (isSubscribed && subscription?.price_id) {
      console.log('Active subscription with price_id:', subscription.price_id);
      
      // Premium (12 meses)
      if (subscription.price_id === 'price_1RtW0mB0B3nriuwY1nJcHogh') {
        return {
          maxGroups: 2,
          maxPlayersPerGroup: 100,
          hasResenha: true,
          canCreateGame: true,
          hasPrioritySupport: true,
          supportPhone: '5511916158282',
          durationMonths: 12,
          planName: 'Premium',
          planType: 'premium'
        };
      }
      
      // Profissional (12 meses)
      if (subscription.price_id === 'price_1RtW7UB0B3nriuwYofTFEbYJ') {
        return {
          maxGroups: 2,
          maxPlayersPerGroup: 50,
          hasResenha: true,
          canCreateGame: true,
          hasPrioritySupport: false,
          supportPhone: '',
          durationMonths: 12,
          planName: 'Profissional',
          planType: 'professional'
        };
      }
      
      // Básico (1 mês)
      if (subscription.price_id === 'price_1RtW9TB0B3nriuwYUyIoSbfp') {
        return {
          maxGroups: 1,
          maxPlayersPerGroup: 30,
          hasResenha: true,
          canCreateGame: true,
          hasPrioritySupport: false,
          supportPhone: '',
          durationMonths: 1,
          planName: 'Básico',
          planType: 'basic'
        };
      }
      
      // Fallback para assinatura desconhecida
      console.log('Unknown subscription price_id, using default limits');
      return {
        maxGroups: 1,
        maxPlayersPerGroup: 30,
        hasResenha: false,
        canCreateGame: false,
        hasPrioritySupport: false,
        supportPhone: '',
        durationMonths: 1,
        planName: 'Assinante',
        planType: 'basic'
      };
    }
    
    // Período de trial (21 dias)
    if (isTrialActive) {
      return {
        maxGroups: 1,
        maxPlayersPerGroup: 30,
        hasResenha: true,
        canCreateGame: true,
        hasPrioritySupport: false,
        supportPhone: '',
        durationMonths: 0, // 0 indica período de trial
        planName: 'Trial',
        planType: 'trial'
      };
    }

    // Plano gratuito (após trial expirado)
    return {
      maxGroups: 1,
      maxPlayersPerGroup: 10,
      hasResenha: false,
      canCreateGame: false,
      hasPrioritySupport: false,
      supportPhone: '',
      durationMonths: 0,
      planName: 'Gratuito',
      planType: 'free'
    };
  };

  const limits = getPlanLimits();

  // Function to validate group creation, with message
  const canCreateGroup = async (
    currentGroupCount: number
  ): Promise<{ allowed: boolean; message?: string }> => {
    if (currentGroupCount >= limits.maxGroups) {
      return {
        allowed: false,
        message: `Seu plano ${limits.planName} permite até ${limits.maxGroups} ${limits.maxGroups === 1 ? 'grupo' : 'grupos'}. Faça upgrade para criar mais grupos.`
      };
    }
    return { allowed: true };
  };

  // Function to validate adding players to group
  const canAddPlayer = async (
    currentPlayerCount: number
  ): Promise<{ allowed: boolean; message?: string }> => {
    if (currentPlayerCount >= limits.maxPlayersPerGroup) {
      if (limits.hasPrioritySupport && limits.supportPhone) {
        return {
          allowed: false,
          message: `Você atingiu o limite de ${limits.maxPlayersPerGroup} jogadores do plano ${limits.planName}. Entre em contato com o suporte: +${limits.supportPhone}`
        };
      }
      return {
        allowed: false,
        message: `Seu plano ${limits.planName} permite até ${limits.maxPlayersPerGroup} jogadores. Faça upgrade para adicionar mais jogadores.`
      };
    }
    return { allowed: true };
  };

  return {
    limits,
    canCreateGroup,
    canAddPlayer,
    isTrialActive,
    isSubscribed,
    isLifetime,
    loading,
    error
  };
};